import React, { useEffect, useState } from "react";
import Header from '../header/header';
import "./styles.css";
import InformationCard from "../informationCard/informationCard";
import { Typography, Box, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PrintDialogBox from "./printDialogBox";
import axios from "axios";
import config from "../../config";
import { getCurrentUserDashboardStats, getCurrentUserId, db, getLastSyncTime, saveFundusCameraList, getFundusCameraList, clearScreeningImageDraft } from "../../db";
import SelectFundusCamera from "../FundusCameraSelectionComponent/SelectFundusCamera";
import { isOffline, clearOfflineCache } from "../../utils/offlineDetection";
import { initOpenCVWorker } from "../../services/opencvWorkerManager";

const Dashboard = () => {
    const navigate = useNavigate();
    const header = { "Authorization": "Token " + localStorage.getItem("optha-token") };
    const [isDialog, setIsDialog] = useState(false);
    const [statsData, setStatsData] = useState({});
    const [openFundusCameraDialog, setOpenFundusCameraDialog] = useState(false);
    const [fundusCameras, setFundusCameras] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [redirectToPatientScreen, setRedirectToPatientScreen] = useState(false);
    const [offline, setOffline] = useState(false);
    const [cacheLoaded, setCacheLoaded] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);

    useEffect(() => {
        const checkConnectionStatus = async () => {
            const offlineStatus = await isOffline();
            setOffline(offlineStatus);
        };

        // Initial check
        checkConnectionStatus();
        
        // Check periodically (every 10 seconds)
        const interval = setInterval(checkConnectionStatus, 10000);
        
        // Listen to browser events for immediate updates
        const handleOnline = async () => {
            clearOfflineCache();
            await checkConnectionStatus();
        };
        
        const handleOffline = () => {
            setOffline(true);
            // Verify with API check
            checkConnectionStatus();
        };
        
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        
        return () => {
            clearInterval(interval);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        localStorage.removeItem("patient_id");
        localStorage.removeItem("is_follow_up_patient"); // Clear follow-up flag when starting new patient
        localStorage.removeItem("follow_up_other_id"); // Clear follow-up otherId when starting new patient
        localStorage.removeItem("patient_form_draft"); // Clear form draft when starting new patient
        localStorage.removeItem("report_id"); // Clear report_id when navigating to dashboard
        clearScreeningImageDraft().catch(() => {});
        
        // Set up axios interceptor for offline handling when Dashboard is mounted
        const interceptor = axios.interceptors.request.use(
            function(config) {
                // Don't block API calls based on navigator.onLine as it's unreliable
                // Let the actual network request determine if we're offline
                return config;
            },
            function(error) {
                return Promise.reject(error);
            }
        );
        
        // Cleanup interceptor when component unmounts
        return () => {
            axios.interceptors.request.eject(interceptor);
        };
    }, []);

    // Load last sync time
    useEffect(() => {
        const loadLastSyncTime = async () => {
            try {
                const syncTime = await getLastSyncTime();
                setLastSyncTime(syncTime);
            } catch (error) {
                console.error('Error loading last sync time:', error);
            }
        };
        
        loadLastSyncTime();
        
        // Refresh sync time when component becomes visible (user navigates back)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                loadLastSyncTime();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const loadDashboardData = async () => {
            // Use offline state (already verified via API)
            const isReallyOffline = offline;
            
            if (isReallyOffline) {
                // Load from IndexDB cache when offline
                try {
                    const cachedStats = await getCurrentUserDashboardStats();
                    if (cachedStats) {
                        setStatsData(cachedStats.data);
                        setCacheLoaded(true);
                        setLastSyncTime(cachedStats.lastUpdated);
                    } else {
                        setCacheLoaded(false);
                    }
                } catch (error) {
                    console.error('Error loading cached dashboard stats:', error);
                    setCacheLoaded(false);
                }
                return;
            }

            // Online: fetch from API and update IndexDB
            try {
                const response = await axios.get(`${config.API_BASE_URL}/api/reports/dashboard/`, { headers: header });
                const newStatsData = response.data;
                
                // Update state
                setStatsData(newStatsData);
                setCacheLoaded(true);
                
                // Save to IndexDB with user ID
                const userId = getCurrentUserId();
                const currentTime = new Date().toISOString();
                await db.dashboard_stats.put({
                    id: 'dashboard_stats',
                    data: newStatsData,
                    lastUpdated: currentTime,
                    optometrist_id: userId
                });
                
                setLastSyncTime(currentTime);
            } catch (error) {
                console.error('Error fetching dashboard stats:', error);
                setCacheLoaded(false);
                
                // Handle network errors gracefully
                if (error.code === 'ERR_NETWORK' || 
                    error.message === 'Network Error' ||
                    error.message.includes('ERR_INTERNET_DISCONNECTED')) {
                }
                
                // Try to load from cache if API fails
                try {
                    const cachedStats = await getCurrentUserDashboardStats();
                    if (cachedStats) {
                        setStatsData(cachedStats.data);
                        setCacheLoaded(true);
                        setLastSyncTime(cachedStats.lastUpdated);
                    }
                } catch (cacheError) {
                    console.error('Error loading cached stats after API failure:', cacheError);
                }
            }
        };
        
        loadDashboardData();
    }, [offline]);
    
    // Check if fundus camera is already selected
    useEffect(() => {
        const fetchCameras = async () => {
          try {
            const userProfile = JSON.parse(localStorage.getItem("user_profile"));
            const health_facility_id = userProfile?.health_facility?.id;
      

      
            /* ================= OFFLINE ================= */
            if (offline) {
      
              const savedCameraData = await getFundusCameraList(health_facility_id);
      
      
              const savedCameraList = Array.isArray(savedCameraData?.cameras)
                ? savedCameraData.cameras
                : [];
      
      
              if (savedCameraList.length > 0) {
                setFundusCameras(savedCameraList);
      
                if (savedCameraList.length === 1) {
                  localStorage.setItem("fundusCamera", savedCameraList[0].id);
                } else if (!localStorage.getItem("fundusCamera")) {
                  setOpenFundusCameraDialog(true);
                }
              } else {
                console.warn("⚠️ No cameras found in IndexedDB");
              }
      
              return;
            }
      
            /* ================= ONLINE ================= */
      
            const response = await axios.get(
              `${config.API_BASE_URL}/api/facilities/fundus-camera/?facility=${health_facility_id}`,
              { headers: header }
            );
      
      
            const cameraList = response.data?.results || [];
      
            setFundusCameras(cameraList);
      
            await saveFundusCameraList(health_facility_id, cameraList);

            if (cameraList.length === 1) {
              localStorage.setItem("fundusCamera", cameraList[0].id);
            } else if (!localStorage.getItem("fundusCamera")) {
              setOpenFundusCameraDialog(true);
            }
      
          } catch (error) {
            console.error("❌ fetchCameras failed:", error);
          }
        };
      
        fetchCameras();
      }, [offline]);
      
      
      

    const handleFundusCameraSave = (selectedCamera) => {
        localStorage.setItem("fundusCamera", selectedCamera);
        setOpenFundusCameraDialog(false);

        if (redirectToPatientScreen) {
            initOpenCVWorker().catch((err) => {
                console.error('OpenCV worker init failed:', err);
            });
            navigate("/patientscreen");
            setRedirectToPatientScreen(false);
        }
    };

    

    // const stats_info = [
    //     { "heading": "Total Patients Screened", "number": "", "time": "Since Jan 2023", "key": "total_patients_screened" },
    //     { "heading": "Total DR Positive Patients", "number": "", "time": "Cases Since Jan 2023", "key": "total_positive_patients" },
    //     { "heading": "Total Referable Patients", "number": "", "time": "Cases Since Jan 2023", "key": "total_referable_patients" },
    //     { "heading": "Average Patients Per Day", "number": "", "time": "Average Patients Screened Per day", "key": "average_patients_per_day" }
    // ];

    const stats_info = [
        {
            heading: "Total Patients Screened",
            number: "",
            timeKey: "patient_screening_start_date", 
            key: "total_patients_screened"
        },
        {
            heading: "Total DR Positive Patients",
            number: "",
            timeKey: "dr_positive_patients_start_date", 
            key: "total_positive_patients"
        },
        {
            heading: "Total Referable Patients",
            number: "",
            timeKey: "referable_patients_start_date",
            key: "total_referable_patients"
        },
        {
            heading: "Average Patients Screened Per Day",
            number: "",
            time: "Only active screening days considered", 
            key: "average_patients_per_day"
        }
    ];
    

    const addPatient = async () => {
        localStorage.removeItem("patient_id");
        localStorage.removeItem("report_id");
        localStorage.removeItem("patient_form_draft");
        localStorage.removeItem("is_follow_up_patient");
        localStorage.removeItem("follow_up_other_id");
        try {
            await clearScreeningImageDraft();
        } catch (_) { /* best-effort */ }

        initOpenCVWorker().catch((err) => {
            console.error('OpenCV worker init failed:', err);
        });

        if (!localStorage.getItem("fundusCamera")) {
            setRedirectToPatientScreen(true); // Mark that user wants to go to patient screen
            setOpenFundusCameraDialog(true);
        } else {
            navigate("/patientscreen");
        }
    };

    // Format last sync time for display
    const formatLastSyncTime = (syncTime) => {
        if (!syncTime) return "Never synced";
        
        const syncDate = new Date(syncTime);
        
        if (isNaN(syncDate.getTime())) {
            return "Never synced";
        }
        
        // Format: "Nov 19 2025, 2:30 PM" (Month Date Year, Time)
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[syncDate.getMonth()];
        const day = syncDate.getDate();
        const year = syncDate.getFullYear();
        
        let hours = syncDate.getHours();
        const minutes = syncDate.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const minutesStr = minutes < 10 ? '0' + minutes : minutes;
        
        return `${month} ${day} ${year}, ${hours}:${minutesStr} ${ampm}`;
    };

    return (
        <>
            {!isMobile && <Header />}
            
            {/* Last Sync Time Display */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                p: 2,
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e0e0e0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    backgroundColor: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                }}>
                    <Box 
                        component="span" 
                        sx={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%', 
                            backgroundColor: lastSyncTime ? '#4caf50' : '#ff9800',
                            display: 'inline-block',
                            boxShadow: lastSyncTime ? '0 0 8px rgba(76, 175, 80, 0.3)' : '0 0 8px rgba(255, 152, 0, 0.3)'
                        }} 
                    />
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: '#333',
                            fontWeight: 500,
                            cursor: 'help'
                        }}
                        title={lastSyncTime ? `Last synced: ${formatLastSyncTime(lastSyncTime)}` : 'No sync data available'}
                    >
                        Last synced: {formatLastSyncTime(lastSyncTime)}
                    </Typography>
                </Box>
            </Box>
            
            {/* Show Fundus Camera Selection Dialog if not selected */}
            <SelectFundusCamera
                open={openFundusCameraDialog}
                handleClose={() => setOpenFundusCameraDialog(false)}
                handleSave={handleFundusCameraSave}
                cameras={fundusCameras}
            />

            <div className="dashboardDiv">
                {!isMobile && (
                    <div style={{ width: "36%" ,marginLeft:"80px",marginRight:"40px", marginTop: "7vh", marginBottom: "5vh"}}>
                        <img src="/assests/images/dashboardOptha.svg" alt="Opthalmologist" style={{ width: "100%", height: "100%" }} />
                    </div>
                )}
                <div className="rightcolumn">
                    <div className="informationGrid">
                        {stats_info.map((info, index) => (
                            <InformationCard
                                key={index}
                                heading={info.heading}
                                number={
                                    statsData[info.key] !== undefined
                                        ? Math.round(statsData[info.key])
                                        : ''
                                }
                                time={
                                    info.time
                                      ? info.time
                                      : info.timeKey && statsData[info.timeKey]
                                        ? (index === 0
                                            ? `Since ${statsData[info.timeKey]}`
                                            : (index === 1 || index === 2)
                                              ? `Cases Since ${statsData[info.timeKey]}`
                                              : statsData[info.timeKey]
                                          )
                                        : ''
                                }
                                  
                                percentage={
                                    statsData["total_patients_screened"]
                                        ? Math.round(
                                            (statsData[info.key] / statsData["total_patients_screened"]) * 100
                                        )
                                        : 0
                                }
                                isMobile={isMobile}
                            />
                        ))}
                    </div>

                    <Box className="addDiv" onClick={addPatient}>
                        <Box className="addImg">
                            <img src="/assests/images/addButton.svg" style={{ width: "90%", height: "90%" }} alt="Add" />
                        </Box>
                        <Box className="addtext">
                            <Typography fontSize={{ xs: '16px', md: '20px' }} fontWeight="500">Add Patient Report</Typography>
                            {!isMobile && (
                                <Typography sx={{ opacity: "80%" }} fontSize={{ xs: '12px', md: '14px' }} fontWeight={500}>
                                    Create a patient report with AI screening results and have it shared with an Ophthalmologist in just a few clicks.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </div>
            </div>
            <PrintDialogBox open={isDialog} setIsDialog={setIsDialog} addPatient={addPatient} />
            {offline && !cacheLoaded && (
                <div style={{color: 'red', textAlign: 'center', margin: '1em'}}>Offline: No cached dashboard data available.</div>
            )}
        </>
    );
};

export default Dashboard;
